import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, Switch, Button, Alert, CircularProgress,
    InputAdornment, List, ListItem, ListItemAvatar, ListItemText,
    ListItemSecondaryAction, Chip, Divider, Tooltip,
    TablePagination,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import ConditionBuilder, { buildConditionsJson } from "../rules/ConditionBuilder";
import { segmentErrorMessage } from "./segmentErrorMessage";
import { searchLoyaltyCustomersPaginated } from "../../utils/customerLookup";

// ─── helpers ──────────────────────────────────────────────────────────────────

const avatarLetter = (pid) => (pid ? pid.charAt(0).toUpperCase() : "?");
const avatarColor = (pid) => {
    if (!pid) return "#94a3b8";
    const hue = Math.abs(pid.split("").reduce((acc, c) => acc * 31 + c.charCodeAt(0), 0)) % 360;
    return `hsl(${hue}, 55%, 48%)`;
};

// ─── constants ────────────────────────────────────────────────────────────────

const INITIAL_FORM = { name: "", description: "", is_dynamic: true, active: true };

const SEGMENT_UI_HINTS = {
    field: {
        datasource: {
            endpoint: "/admin/segments/ui-options/condition-fields",
            brandVia: "X-Brand",
            valueField: "items",
        },
        allowCreate: true,
    },
    operator: {
        options: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "in", "contains", "exists"],
    },
};

// ─── CustomerSearchPicker (local-state mode, no segment id yet) ───────────────

const CustomerSearchPicker = ({ brandName, selectedIds, onAdd, onRemove, selectedMembers }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const debounceRef = useRef(null);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [mode, setMode] = useState("list"); // list | search

    const LIMIT = 20;

    const fetchCustomers = useCallback(async ({ q, nextPage }) => {
        setLoading(true);
        setSearchError(null);
        try {
            const offset = (nextPage || 0) * LIMIT;
            const data = await searchLoyaltyCustomersPaginated(brandName, q, { limit: LIMIT, offset });
            setResults(Array.isArray(data.items) ? data.items : []);
            setTotal(Number.isFinite(data.total) ? data.total : (Array.isArray(data.items) ? data.items.length : 0));
        } catch (err) {
            setSearchError(err.message);
        } finally {
            setLoading(false);
        }
    }, [brandName]);

    // initial list load
    useEffect(() => {
        if (!brandName) return;
        if (mode !== "list") return;
        fetchCustomers({ q: "", nextPage: page });
    }, [brandName, mode, page, fetchCustomers]);

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setPage(0);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const hasQ = val.trim().length > 0;
            setMode(hasQ ? "search" : "list");
            fetchCustomers({ q: hasQ ? val : "", nextPage: 0 });
        }, 380);
    };

    return (
        <Box>
            {/* search box */}
            <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par Profile ID Unomi ou email…"
                helperText="Email : résolu via Unomi. Fragment d'UUID : via GET /customers?q="
                value={query}
                onChange={handleQueryChange}
                disabled={!brandName}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                    ) : null,
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            {searchError && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>{searchError}</Alert>
            )}

            {/* search results */}
            {results.length > 0 && (
                <List dense disablePadding sx={{
                    mt: 1, maxHeight: 200, overflowY: "auto",
                    border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
                }}>
                    {results.map((c, idx) => {
                        const already = selectedIds.has(c.id);
                        return (
                            <React.Fragment key={c.id}>
                                {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                    <ListItemAvatar sx={{ minWidth: 36 }}>
                                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(c.profile_id) }}>
                                            {avatarLetter(c.profile_id)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{c.profile_id}</Typography>}
                                        secondary={<Typography fontSize={10} color="text.secondary">{c.loyalty_status_name || c.loyalty_status || "–"} · {c.status_points ?? 0} pts</Typography>}
                                        sx={{ my: 0 }}
                                    />
                                    <ListItemSecondaryAction>
                                        {already ? (
                                            <Chip label="Sélectionné" size="small"
                                                icon={<CheckCircleIcon sx={{ fontSize: "12px !important" }} />}
                                                sx={{ fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a", height: 22 }} />
                                        ) : (
                                            <Tooltip title="Ajouter à la sélection">
                                                <IconButton size="small" onClick={() => onAdd(c)}
                                                    sx={{
                                                        color: SECTION_COLORS.segments.main,
                                                        bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                        width: 26, height: 26,
                                                        "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                    }}>
                                                    <PersonAddIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}

            {!loading && query.trim() && results.length === 0 && (
                <Typography fontSize={12} color="text.secondary" mt={1} textAlign="center">
                    Aucun client trouvé pour « {query} »
                </Typography>
            )}

            {/* pagination only for list mode */}
            {mode === "list" && total > LIMIT && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={LIMIT}
                    rowsPerPageOptions={[LIMIT]}
                    sx={{ mt: 0.5 }}
                />
            )}

            {/* selected members */}
            <Box mt={2}>
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <PeopleAltIcon sx={{ fontSize: 14, color: SECTION_COLORS.segments.main }} />
                    <Typography fontSize={12} fontWeight={700} color="#334155">
                        Membres sélectionnés ({selectedMembers.length})
                    </Typography>
                </Box>

                {selectedMembers.length === 0 ? (
                    <Box py={2} display="flex" alignItems="center" justifyContent="center"
                        sx={{ border: "1px dashed #e2e8f0", borderRadius: 2, bgcolor: "#fafbfd" }}>
                        <Typography fontSize={11} color="text.secondary">
                            Aucun membre sélectionné — recherchez et ajoutez des clients ci-dessus
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{
                        maxHeight: 200, overflowY: "auto",
                        border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
                    }}>
                        {selectedMembers.map((c, idx) => (
                            <React.Fragment key={c.id}>
                                {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                    <ListItemAvatar sx={{ minWidth: 36 }}>
                                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(c.profile_id) }}>
                                            {avatarLetter(c.profile_id)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{c.profile_id}</Typography>}
                                        secondary={<Typography fontSize={10} color="text.secondary">{c.loyalty_status_name || c.loyalty_status || "–"}</Typography>}
                                        sx={{ my: 0 }}
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="Retirer de la sélection">
                                            <IconButton size="small" onClick={() => onRemove(c.id)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", width: 26, height: 26, "&:hover": { bgcolor: "#fee2e2" } }}>
                                                <PersonRemoveIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

// ─── CreateSegmentDialog ──────────────────────────────────────────────────────

const CreateSegmentDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // dynamic mode
    const [combinator, setCombinator] = useState("and");
    const [condRows, setCondRows] = useState([]);

    // static mode — local member list before the segment exists
    const [selectedMembers, setSelectedMembers] = useState([]); // array of CustomerOut

    const selectedIds = new Set(selectedMembers.map((c) => c.id));

    const handleConditionsChange = (newCombinator, newRows) => {
        setCombinator(newCombinator);
        setCondRows(newRows);
    };

    const handleAddMember = (customer) => {
        setSelectedMembers((prev) => {
            if (prev.some((c) => c.id === customer.id)) return prev;
            return [...prev, customer];
        });
    };

    const handleRemoveMember = (customerId) => {
        setSelectedMembers((prev) => prev.filter((c) => c.id !== customerId));
    };

    const isValid = brandName && form.name.trim() && (!form.is_dynamic || condRows.length > 0);

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            // 1. Build conditions
            const conditions = form.is_dynamic ? buildConditionsJson(combinator, condRows) : null;
            if (form.is_dynamic && !conditions) {
                throw new Error("Un segment dynamique doit contenir au moins une condition.");
            }

            // 2. Create segment
            const body = {
                brand: brandName,
                name: form.name.trim(),
                description: form.description.trim() || null,
                is_dynamic: form.is_dynamic,
                conditions,
                active: form.active,
            };
            const createUrl = form.is_dynamic
                ? `${LOYALTY_API_BASE}/admin/segments?recompute=true`
                : `${LOYALTY_API_BASE}/admin/segments`;
            const res = await loyaltyFetch(createUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Brand": brandName },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Création segment"));
            }
            const newSegment = await res.json();

            // 3. Add members in bulk for static mode
            if (!form.is_dynamic && selectedMembers.length > 0) {
                const bulkRes = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/segments/${newSegment.id}/members/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ customer_ids: selectedMembers.map((c) => c.id) }),
                });
                if (!bulkRes.ok) {
                    const e = await bulkRes.json().catch(() => null);
                    throw new Error(segmentErrorMessage(bulkRes.status, e?.detail, "Ajout membres"));
                }
            }

            onCreated();
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setForm({ ...INITIAL_FORM });
        setCombinator("and");
        setCondRows([]);
        setSelectedMembers([]);
        setError(null);
        onClose();
    };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Segment
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    {/* Name */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom du segment" value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            size="small" disabled={!brandName} sx={fieldSx} />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            size="small" multiline rows={2} disabled={!brandName} sx={fieldSx} />
                    </Grid>

                    {/* Dynamic toggle */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Segment dynamique</Typography>
                                <Typography fontSize={11} color="text.secondary">
                                    {form.is_dynamic
                                        ? "Actif : membres calculés automatiquement à partir des conditions"
                                        : "Inactif : vous gérez les membres manuellement"}
                                </Typography>
                            </Box>
                            <Switch
                                checked={form.is_dynamic}
                                onChange={() => setForm((p) => ({ ...p, is_dynamic: !p.is_dynamic }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.segments.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.segments.main },
                                }} />
                        </Box>
                    </Grid>

                    {/* ── Dynamic: condition builder ── */}
                    {form.is_dynamic && (
                        <Grid item xs={12}>
                            <Typography fontSize={13} fontWeight={700} mb={1} color="#334155">
                                Conditions du segment
                            </Typography>
                            <Typography fontSize={11} color="text.secondary" mb={1}>
                                Les membres seront calculés par le moteur fidélité après création (recompute).
                            </Typography>
                            <ConditionBuilder
                                brandName={brandName}
                                transactionType="segment_dynamic"
                                conditionCatalog={[]}
                                uiHints={SEGMENT_UI_HINTS}
                                combinator={combinator}
                                rows={condRows}
                                onChange={handleConditionsChange}
                                hasPayloadSchema={false}
                                payloadPaths={[]}
                            />
                        </Grid>
                    )}

                    {/* ── Static: member picker ── */}
                    {!form.is_dynamic && (
                        <Grid item xs={12}>
                            <Box px={1.5} py={1.5} sx={{
                                borderRadius: 2,
                                border: `1px solid ${SECTION_COLORS.segments.main}25`,
                                bgcolor: `${SECTION_COLORS.segments.main}05`,
                            }}>
                                <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                                    <PeopleAltIcon sx={{ fontSize: 16, color: SECTION_COLORS.segments.main }} />
                                    <Typography fontSize={13} fontWeight={700} color="#334155">
                                        Membres du segment (mode manuel)
                                    </Typography>
                                </Box>
                                <CustomerSearchPicker
                                    brandName={brandName}
                                    selectedIds={selectedIds}
                                    selectedMembers={selectedMembers}
                                    onAdd={handleAddMember}
                                    onRemove={handleRemoveMember}
                                />
                            </Box>
                        </Grid>
                    )}

                    {/* Active toggle */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer le segment</Typography>
                                <Typography fontSize={11} color="text.secondary">
                                    Le segment sera disponible pour les règles et jobs internes
                                </Typography>
                            </Box>
                            <Switch
                                checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.segments.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.segments.main },
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.segments.gradient, color: "#fff",
                        textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.segments.main}40` },
                    }}>
                    {submitting ? "Création…" : "Créer le segment"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateSegmentDialog;
